import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import {
  GroupState,
  OpportunityReviewBoardGroupDetails,
  OpportunityReviewTableContent,
} from '@models/OpportunityReview';
import { useOpportunityReviewGroupState } from '@utils/hook/userPreferences/UseGroupState';
import { PageName, PageType } from '@utils/pageConfig/Types';
import { GroupContext, OPPORTUNITY_REVIEW_BOARD_TABLE } from '@utils/userPreferencesUtils/Types';
import { useSelector } from 'react-redux';
import { useOpportunityReviewBoardGroupEventState } from '../utils/OpportunityReviewBoardGroupContext';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { useState } from 'react';
import { getGrandTotalValue } from '@utils/TotalUtils';
import {
  groupElementClickedTrackingFunction,
  OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import * as styles from '@utils/tableViewscss/TableViewBoard.module.scss';
import OpportunityReviewTableRow from '@components/opportunity-review-board/opportunity-review-table-row/OpportunityReviewTableRow';
import classnames from 'classnames';
import { ColumnConfig, TableBody } from '@components/design/migrated/Table';

interface Props {
  groupDetails: OpportunityReviewBoardGroupDetails;
  opportunityReviewBoardPgs: OpportunityReviewTableContent[];
  currencySymbol: string;
  columns: ColumnConfig[];
  sortedColumn: string;
  selectedGrowthType: GrowthTypes;
  context: GroupContext;
  isGroupingHeader?: boolean;
  isRowHovered?: boolean;
  groupState?: GroupState;
  isPreferenceOwner?: boolean;
}

export const OpportunityReviewTableGroupRow: React.FC<Props> = ({
  groupDetails,
  opportunityReviewBoardPgs,
  currencySymbol,
  columns,
  sortedColumn,
  selectedGrowthType,
  context,
  isGroupingHeader,
  isRowHovered,
  isPreferenceOwner,
  groupState,
}) => {
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const { updateOpportunityReviewGroup } = useOpportunityReviewGroupState(context);
  const { isEditGroupMode, setIsEditGroupMode, currentSelectedFeature } = useOpportunityReviewBoardGroupEventState();
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();

  const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);

  const CURRENT_TOTAL_NAME = getGrandTotalValue();

  const getGroupTotalKpi: OpportunityReviewTableContent | undefined = opportunityReviewBoardPgs.find(
    (opportunityReviewBoardPg) => opportunityReviewBoardPg.segment === CURRENT_TOTAL_NAME,
  );
  const getGroupsKpi: OpportunityReviewTableContent[] = opportunityReviewBoardPgs.filter(
    (opportunityReviewBoardPg) => opportunityReviewBoardPg.segment !== CURRENT_TOTAL_NAME,
  );

  const getOpportunityReviewBoardPgWithGroupName = (): OpportunityReviewTableContent => {
    const displayGroupNameWithPgsCount = `${groupDetails.groupName} (${getGroupsKpi.length})`;

    return { ...getGroupTotalKpi!, segment: displayGroupNameWithPgsCount };
  };

  const isGroupEmpty: boolean = getGroupsKpi.length === 0;

  const constructAndUpdateOpportunityReviewGroup = (segmentToBeRemove: string) => {
    updateOpportunityReviewGroup.mutate({
      key: groupDetails.groupName,
      data: {
        productGroup: groupDetails.groupElement.filter((segment) => segment !== segmentToBeRemove),
        value: groupDetails.groupElement.filter((segment) => segment !== segmentToBeRemove),
        groupElement: groupDetails.groupElement.filter((segment) => segment !== segmentToBeRemove),
        showPerformanceWithin: currentSelectedFeature.feature,
        sortOrder: groupDetails.sortOrder!,
      },
    });
  };

  const onDeleteGroupPgs = (pgToBeRemove: string) => {
    //Amplitude Tracking when Remove pgs icon is clicked
    const trackingParam: TableElementClickedParam = {
      eventTitle: OPPORTUNITY_REVIEW_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Delete ${currentSelectedFeature.feature} Group`,
      elementType: `Table ${currentSelectedFeature.feature} Group`,
      track: track,
      optionChosen: pgToBeRemove,
      pageName: PageName.OPPORTUNITY_REVIEW,
      pageType: PageType.OPPORTUNITY_REVIEW,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: OPPORTUNITY_REVIEW_BOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    constructAndUpdateOpportunityReviewGroup(pgToBeRemove);
  };

  const groupStateForGroupHeader: GroupState = {
    groupName: groupDetails.groupName,
    enableIsEditGroupMode: () =>
      setIsEditGroupMode({
        groupDetails: groupDetails,
        isEditMode: true,
      }),
    isGroupingHeader: true,
    isGroupEmpty: isGroupEmpty,
    setIsGroupingRowExpanded: setIsRowExpanded,
    isPreferenceOwner: groupDetails.isPreferenceOwner,
  };

  const groupStateForGroupBody: GroupState = {
    groupName: groupDetails.groupName,
    isGroupingProductGroup: true,
    onDeleteGroupPgs: onDeleteGroupPgs,
    isPreferenceOwner: groupDetails.isPreferenceOwner,
  };
  const isDisableGroups = () => {
    return isEditGroupMode.isEditMode && isEditGroupMode.groupDetails.groupName !== groupDetails.groupName;
  };

  return (
    <TableBody>
      {getOpportunityReviewBoardPgWithGroupName() && (
        <OpportunityReviewTableRow
          key={groupDetails.groupName}
          className={classnames([
            styles.TableViewboard__TableRow__WithHover,
            { [styles['TableViewboard__TableRow__isDisabled']]: isDisableGroups() },
          ])}
          opportunityReviewBoardPg={getOpportunityReviewBoardPgWithGroupName()}
          columns={columns}
          sortedColumn={sortedColumn}
          selectedGrowthType={selectedGrowthType}
          currencySymbol={currencySymbol}
          groupState={groupStateForGroupHeader}
          isPreferenceOwner={groupDetails.isPreferenceOwner}
        ></OpportunityReviewTableRow>
      )}
      {isRowExpanded &&
        !isEditGroupMode.isEditMode &&
        getGroupsKpi.map((content) => (
          <OpportunityReviewTableRow
            key={`${groupDetails.groupName} ${content.segment}`}
            className={styles.TableViewboard__TableRow__WithHover}
            opportunityReviewBoardPg={content}
            columns={columns}
            sortedColumn={sortedColumn}
            selectedGrowthType={selectedGrowthType}
            currencySymbol={currencySymbol}
            groupState={groupStateForGroupBody}
            isPreferenceOwner={groupDetails.isPreferenceOwner}
          ></OpportunityReviewTableRow>
        ))}
    </TableBody>
  );
};
